import { ArrowRightIcon, BrandLogoIcon, Button, CheckIcon, Spinner, Wizard } from '@st/theme'
import { match, P } from '@st/pattern'
import type { ProConnectImportMessage, ProConnectImportState } from './pro-connect-import-module'

export function ProConnectImportPage({
  state,
  send,
  onBack,
  onClose
}: {
  state: ProConnectImportState
  send: (message: ProConnectImportMessage) => void
  onBack: () => void
  onClose: () => void
}) {
  return (
    <Wizard.Scaffold
      breadcrumb="Generate binders"
      bottomNav={
        <Wizard.BottomNav>
          <Button size="l" variant="default" onClick={onBack} disabled={isBusy(state)}>
            Back
          </Button>
        </Wizard.BottomNav>
      }
      onClose={onClose}
    >
      {match(state)
        .with({ status: P.union('loadingConnection', 'acquiringReturns') }, () => (
          <LoadingContent />
        ))
        .with({ status: 'authorizeConnection' }, (state) => (
          <Wizard.Notice
            title="Connect ProConnect to StanfordTax"
            icon={
              <div className="flex items-center gap-3">
                <img src="/logos/proconnect.png" alt="ProConnect" className="size-12 rounded-md" />
                <ArrowRightIcon className="size-6 text-gray-500" />
                <BrandLogoIcon />
              </div>
            }
          >
            <div className="mx-auto flex w-full max-w-md flex-col items-center gap-3 text-center">
              <p className="text-base text-gray-600">StanfordTax will be able to:</p>

              <ul className="my-5 w-full space-y-4 text-left">
                <li className="flex gap-x-3 text-base">
                  <CheckIcon aria-hidden="true" className="h-6 w-5 flex-none text-blue-600" />
                  View your ProConnect clients and tax returns
                </li>
                <li className="flex gap-x-3 text-base">
                  <CheckIcon aria-hidden="true" className="h-6 w-5 flex-none text-blue-600" />
                  Download your eligible {state.taxYear} individual returns
                </li>
              </ul>

              <Button
                size="m"
                className="w-full"
                variant="primary"
                onClick={() => send({ type: 'connectRequested' })}
                disabled={state.connectionStatus == 'redirecting'}
              >
                {match(state.connectionStatus)
                  .with('redirecting', () => 'Connecting…')
                  .with('idle', () => 'Continue to ProConnect')
                  .exhaustive()}
              </Button>
            </div>
          </Wizard.Notice>
        ))
        .exhaustive()}
    </Wizard.Scaffold>
  )
}

function LoadingContent() {
  return (
    <Wizard.Notice
      title="Fetching data from ProConnect…"
      icon={<Spinner className="size-12 fill-blue-500 text-blue-100" />}
    />
  )
}

function isBusy(state: ProConnectImportState) {
  return (
    state.status == 'acquiringReturns' ||
    (state.status == 'authorizeConnection' && state.connectionStatus == 'redirecting')
  )
}
